/**
 * localModelTransform – put a viewer's own size and turn onto a docked model.
 *
 * Students shrink, expand and turn their own copy of a model (see
 * lib/classroom/modelPrecedence for who wins against the teacher). The amounts
 * are kept RELATIVE to what the dock gave each asset — `userData.dockScale` and
 * `userData.dockYaw`, recorded by the player's layout pass — because assets
 * arrive at wildly different authored sizes, and because a relayout (entering
 * the headset, recentring) has to be able to re-place the model and then put
 * the student's changes back on top rather than lose them.
 *
 * Both act about the model's middle rather than its origin. An asset's origin
 * is wherever its author left it — often at one end, or at its feet — and
 * scaling or turning about it pushes the model down through the desk or swings
 * it off its slot.
 *
 * Plus `readStick`, the per-controller thumbstick read the gesture code shares.
 *
 * Scratch objects are module-level: these run every frame of a gesture, and a
 * fresh Vector3 per frame is garbage the collector eventually stops a frame to
 * clear — a visible hitch in a headset. Tested in tests/localModelTransform.test.mts.
 */

import * as THREE from 'three';
import { scaleAboutPivot } from './twoPointGesture';

const box = new THREE.Box3();
const pivot = new THREE.Vector3();
const centre = new THREE.Vector3();

/**
 * Size one asset at `multiplier` times the scale the dock gave it.
 *
 * About the middle of its base while it stands on the dock, so growing a model
 * never sinks it into the desk; about its own origin while it is held, where
 * the hand is the anchor. An asset the dock never placed has no dockScale to be
 * relative to and is left alone.
 *
 * The controller hover highlight remembers the scale it grew a model from
 * (`userData.originalScale`) and puts that back when the ray leaves, so the
 * memory moves with the resize — otherwise the model would snap back to its old
 * size the moment the student looked away from it.
 *
 * Assumes the asset's parent carries no transform (the player's assetsGroup
 * does not), so a world-space pivot is also a parent-space one.
 */
export function applyAssetLocalScale(asset: THREE.Object3D, multiplier: number, aboutBase: boolean): void {
  const dockScale = asset.userData.dockScale;
  if (!(typeof dockScale === 'number' && dockScale > 0) || !(multiplier > 0)) return;
  const next = dockScale * multiplier;
  if (aboutBase) {
    asset.updateMatrixWorld(true);
    box.setFromObject(asset);
    if (!box.isEmpty()) {
      box.getCenter(pivot);
      pivot.y = box.min.y;
      scaleAboutPivot(asset.position, pivot, asset.scale.x, next);
    }
  }
  asset.scale.setScalar(next);
  (asset.userData.originalScale as THREE.Vector3 | undefined)?.setScalar(next);
  asset.updateMatrixWorld(true);
}

/**
 * Turn one asset to `yaw` radians past the way the dock faced it, keeping the
 * middle of its bounding box where it was on the floor plane.
 */
export function applyAssetLocalYaw(asset: THREE.Object3D, yaw: number): void {
  if (!Number.isFinite(yaw)) return;
  asset.updateMatrixWorld(true);
  box.setFromObject(asset);
  const measured = !box.isEmpty();
  if (measured) box.getCenter(pivot);
  const dockYaw = typeof asset.userData.dockYaw === 'number' ? asset.userData.dockYaw : 0;
  asset.rotation.y = dockYaw + yaw;
  asset.updateMatrixWorld(true);
  if (!measured) return;
  box.setFromObject(asset).getCenter(centre);
  asset.position.x += pivot.x - centre.x;
  asset.position.z += pivot.z - centre.z;
  asset.updateMatrixWorld(true);
}

/**
 * An asset's size and turn as `scale` and `yaw` say for it: the dock's size
 * and facing when absent. Turn first, so the base the resize is measured about
 * is the base in the orientation the model is shown in.
 */
export function applyAssetOverrides(
  asset: THREE.Object3D,
  overrides: { scale: Readonly<Record<string, number>>; yaw: Readonly<Record<string, number>> },
  key: string
): void {
  applyAssetLocalYaw(asset, overrides.yaw[key] ?? 0);
  applyAssetLocalScale(asset, overrides.scale[key] ?? 1, true);
}

/** The subset of an XRInputSource `readStick` needs; keeps the test free of WebXR types. */
interface StickSource {
  hand?: unknown;
  gamepad?: { axes: ReadonlyArray<number> } | null;
}

/**
 * A controller's OWN thumbstick, from `userData.inputSource`, written into
 * `out`; false when it has none.
 *
 * The player used to take one reading from whichever input source came last,
 * so the left stick did nothing while the right controller was connected, and
 * a tracked hand (a gamepad with no sticks) could zero both. Reading per
 * controller gives each stick to the controller it is on. A hand is never a
 * stick.
 */
export function readStick(
  controller: THREE.Object3D | null | undefined,
  out: { x: number; y: number }
): boolean {
  out.x = 0;
  out.y = 0;
  const source = controller?.userData.inputSource as StickSource | null | undefined;
  const axes = source && !source.hand ? source.gamepad?.axes : undefined;
  if (!axes) return false;
  // xr-standard puts the thumbstick on axes 2/3; older mappings on 0/1.
  if (axes.length >= 4) {
    out.x = axes[2] || 0;
    out.y = axes[3] || 0;
  } else if (axes.length >= 2) {
    out.x = axes[0] || 0;
    out.y = axes[1] || 0;
  } else {
    return false;
  }
  return true;
}
